<template>
  <div role="dialog" class="modal tooth-svc-modal d-block" aria-modal="true" @keydown.esc="closeModal">
    <div class="modal-dialog modal-lg">
      <div tabindex="-1" class="modal-content">
        <header class="modal-header align-items-start">
          <div>
            <h5 class="modal-title">{{ $t('ServiceSelection') }} ({{ selectedTeeth.length }})</h5>
            <div class="tooth-svc-modal__teeth">
              <span class="tooth-svc-modal__teeth-label">{{ $t('ToothNumber') }}</span>
              <span v-for="toothNumber in sortedTeeth" :key="toothNumber" class="tooth-chip">{{ toothNumber }}</span>
            </div>
          </div>
          <button type="button" class="close" :aria-label="$t('Cancel')" @click="closeModal()">
            <span aria-hidden="true">&times;</span>
          </button>
        </header>

        <div class="modal-body">
          <b-row class="align-items-end">
            <b-form-group class="col-md-8" :label="$t('Staff')">
              <v-select transition="" v-model="staffId"
                        :reduce="s => s.id" label="name"
                        :clearable="false"
                        :options="staffList"
                        @input="onStaffChanged">
                <template v-slot:no-options>
                  {{ $t('NoMatchingOptionsMessage') }}
                </template>
              </v-select>
            </b-form-group>
            <b-col md="4" class="text-md-right mb-3">
              <b-button variant="outline-primary" size="sm" @click="addEntityRow()">
                <i class="fa fa-plus" aria-hidden="true"></i> {{ $t('AddNewLine') }}
              </b-button>
            </b-col>
          </b-row>

          <div class="tooth-svc-modal__rows">
            <tooth-treatment-service-row v-for="(row, rowIndex) in serviceRows" :key="row.key"
                                         :row="row"
                                         :row-index="rowIndex"
                                         :row-count="serviceRows.length"
                                         :entity-list="entityList"
                                         :currency-list="currencyList"
                                         :show-pricing="showPricing"
                                         @entity-selected="onEntitySelected"
                                         @remove="removeRow" />
          </div>

          <b-form-group :label="$t('ToothNote')" class="mt-2 mb-0">
            <textarea v-model="toothNotes" rows="3" class="form-control" :maxlength="toothNoteMaxLength"></textarea>
            <small v-if="toothNotes.length >= toothNoteMaxLength" class="text-danger">{{ $t('ToothTreatmentModalMessage') }}</small>
          </b-form-group>

          <div v-if="showPricing && hasAnySalesRow" class="tooth-svc-modal__total">
            <span>{{ $t('Total') }}</span>
            <strong>{{ totalText }} {{ mainCurrencySymbol }}</strong>
          </div>
        </div>

        <div class="modal-footer">
          <b-button variant="light" @click="closeModal()">{{ $t('Cancel') }}</b-button>
          <b-button variant="primary" :disabled="!hasAnySalesRow || !selectedStaff" @click="submitServiceRows()">{{ $t('Save') }}</b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import ToothTreatmentServiceRow from './ToothTreatmentServiceRow'
  import { TOOTH_TREATMENT_PRICING_ENABLED, TOOTH_NOTE_MAX_LENGTH, calculateRowTotal, convertToMainCurrency, formatAmount } from './toothConfig'
  import { getUniqueString } from '../../utils/stringExtensions'

  import './tooth-chip.css'
  import toothService from '../../services/tooth'
  import staffService from '../../services/staff'
  import customerSketchService from '../../services/customerSketch'

  export default {
    name: 'ToothTreatmentService',
    components: { ToothTreatmentServiceRow },
    props: {
      customerId: { type: String, required: true },
      selectedTeeth: { type: Array, required: true },
      // Seçim modunda eklenen satırların ortak grup kimliği; tek diş tıklamasında null.
      groupId: { type: String, default: null },
      // Fiyat / kur / indirim / vadeli fiyat alanları (bkz. toothConfig).
      showPricing: { type: Boolean, default: TOOTH_TREATMENT_PRICING_ENABLED },
      // Kur listesi, pariteler ve indirim grubu kararı editörde bir kez yüklenir/sorulur.
      currencyList: { type: Array, default: () => [] },
      parityList: { type: Array, default: () => [] },
      // Kabul edilen indirim grubu ("%10" / "10"); yoksa boş.
      customerDiscount: { type: String, default: '' },
      // Kaydet: eklenecek detay satırlarını (diş x satır) alır.
      onSubmit: { type: Function, required: true }
    },
    data() {
      return {
        staffId: '',
        staffList: [],
        entityList: [],
        serviceRows: [],
        toothNotes: '',
        toothNoteMaxLength: TOOTH_NOTE_MAX_LENGTH
      }
    },
    computed: {
      tenantInfo() {
        return this.$store.getters['Auth/tenantInfo'] || {}
      },
      mainCurrencyId() {
        return this.tenantInfo.mainCurrencyId
      },
      mainCurrencySymbol() {
        return this.tenantInfo.mainCurrencySymbol || ''
      },
      selectedStaff() {
        return this.staffList.find(s => s.id == this.staffId) || null
      },
      sortedTeeth() {
        return this.selectedTeeth.slice().sort((a, b) => a - b)
      },
      filledRows() {
        return this.serviceRows.filter(r => r.entityId)
      },
      hasAnySalesRow() {
        return this.filledRows.length > 0
      },
      totalText() {
        const perTooth = this.filledRows.reduce((sum, r) => sum + convertToMainCurrency(r.rowTotal, r.currencyId, this.mainCurrencyId, this.parityList), 0)
        return formatAmount(perTooth * this.selectedTeeth.length)
      }
    },
    methods: {
      newRow(overrides) {
        return Object.assign({
          key: getUniqueString(),
          entityId: '',
          entity: { entityType: '', id: '', name: '', basePrice: 0, basePriceWithCard: 0, currencyId: this.mainCurrencyId },
          quantity: 1,
          userInputDiscount: this.customerDiscount || '',
          useCreditPrice: false,
          currencyId: this.mainCurrencyId,
          rowTotal: '0.00'
        }, overrides || {})
      },
      addEntityRow(overrides) {
        const row = this.newRow(overrides)
        row.rowTotal = calculateRowTotal(row)
        this.serviceRows.push(row)
      },
      removeRow(row) {
        this.serviceRows.splice(this.serviceRows.indexOf(row), 1)
        if (this.serviceRows.length === 0) this.addEntityRow()
      },
      getInitialStaff() {
        customerSketchService.getAppointmentStaffForCustomerSketch(this.customerId)
          .then(response => {
            const currentStaff = this.$store.getters['Auth/currentStaff'] || {}
            this.staffId = response || currentStaff.id || ''
          })
          .catch(() => {
            const currentStaff = this.$store.getters['Auth/currentStaff'] || {}
            this.staffId = currentStaff.id || ''
          })
          .finally(() => {
            this.getStaffList()
            this.getEntityList()
          })
      },
      getStaffList() {
        staffService.getStaffList({ params: { id: this.staffId || null, forSaleRep: 0, onlyShowInCalendar: 0 } })
          .then(response => { this.staffList = response || [] })
      },
      getEntityList() {
        toothService.getServiceAndProductList(this.staffId)
          .then(response => {
            this.entityList = response || []
            // Personel değişince yeni personelin yapamadığı hizmet satırları düşer.
            this.serviceRows = this.serviceRows.filter(r => !r.entityId || this.entityList.some(e => e.id == r.entityId))
            if (this.serviceRows.length === 0) this.addEntityRow()
          })
      },
      onStaffChanged() {
        this.getEntityList()
      },
      onEntitySelected({ entity }) {
        if (entity.entityType !== 'service') return

        toothService.getServiceWithRelatedProducts(entity.id).then(response => {
          const related = (response && response.serviceRelatedProducts) || []
          related.forEach(el => {
            if (!el.product) return
            const product = this.entityList.find(e => e.id == el.product.id) || {
              id: el.product.id,
              entityType: 'product',
              name: el.product.name,
              basePrice: el.product.price,
              basePriceWithCard: el.product.priceWithCard,
              currencyId: el.product.currencyID
            }
            this.addEntityRow({
              entityId: product.id,
              entity: product,
              quantity: el.quantity || 1,
              currencyId: product.currencyId || el.product.currencyID || this.mainCurrencyId
            })
          })
        })
      },
      submitServiceRows() {
        const staff = this.selectedStaff
        if (!staff || !this.hasAnySalesRow) {
          this.$toastr.error(this.$t('ValidationMessage.EmptyAndValidateError'))
          return
        }
        const details = []

        this.filledRows.forEach(row => {
          const entity = row.entity || {}
          const isService = entity.entityType === 'service'
          const currency = this.currencyList.find(c => c.currencyId == row.currencyId)

          this.sortedTeeth.forEach(toothNumber => {
            details.push({
              tempId: getUniqueString(),
              staffId: this.staffId,
              staffName: staff.name,
              toothNumber: toothNumber,
              toothNotes: this.toothNotes,
              serviceId: isService ? entity.id : null,
              serviceName: isService ? entity.name : null,
              productId: isService ? null : entity.id,
              productName: isService ? null : entity.name,
              entityType: entity.entityType,
              price: row.useCreditPrice ? entity.basePriceWithCard : entity.basePrice,
              currencyId: row.currencyId,
              currencySymbol: currency ? currency.symbol : (row.currencyId == this.mainCurrencyId ? this.mainCurrencySymbol : ''),
              quantity: row.quantity,
              userInputDiscount: row.userInputDiscount,
              useCreditPrice: row.useCreditPrice,
              rowTotal: row.rowTotal,
              groupId: this.groupId
            })
          })
        })

        this.onSubmit(details)
        this.closeModal()
      },
      closeModal() {
        this.$modal.hide('vue-modal')
      }
    },
    mounted() {
      this.addEntityRow()
      this.getInitialStaff()
    }
  }
</script>

<style scoped>
  .tooth-svc-modal {
    overflow-y: auto;
  }

  .tooth-svc-modal .modal-content {
    border: 0;
    border-radius: 16px;
  }

  .tooth-svc-modal__teeth {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
  }

  .tooth-svc-modal__teeth-label {
    font-size: 12px;
    color: #777d74;
    margin-right: 4px;
  }


  .tooth-svc-modal__rows {
    padding: 12px;
    border: 1px solid #eef2f3;
    border-radius: 12px;
    background: #fbfdfd;
  }

  .tooth-svc-modal__total {
    display: flex;
    justify-content: flex-end;
    align-items: baseline;
    gap: 10px;
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid #eef2f3;
    color: #374948;
  }

  .tooth-svc-modal__total strong {
    font-size: 18px;
    color: #089bab;
  }
</style>
